import { ReactNode } from 'react';

import { CognitoUserSession } from 'amazon-cognito-identity-js';
import { HelmetProvider } from 'react-helmet-async';

import { AccessibilityProvider } from 'state/accessibility';
import { AuthProvider } from 'state/auth';
import { BranchProvider } from 'state/branch';
import { BrazeProvider } from 'state/braze';
import { CustomizableContentProvider } from 'state/customizable-content';
import { DayPartProvider } from 'state/day-part';
import { DeliveryConfirmationProvider } from 'state/delivery-confirmation';
import { ErrorsProvider } from 'state/errors';
import { FeaturesProvider } from 'state/features';
import { FraudPreventionProvider } from 'state/fraud-prevention';
import { GeolocationProvider } from 'state/geolocation';
import { GraphQLProvider } from 'state/graphql';
import { IntlProvider } from 'state/intl';
import { LDProvider } from 'state/launchdarkly';
import { LoadingProvider } from 'state/loading';
import { LocationProvider } from 'state/location';
import { LoggerProvider } from 'state/logger';
import { LoyaltyProvider } from 'state/loyalty';
import { MParticleProvider } from 'state/mParticle';
import { MenuProvider } from 'state/menu';
import { NetworkProvider } from 'state/network';
import { OffersProvider } from 'state/offers';
import { OnboardingProvider } from 'state/onboarding';
import { OrderProvider } from 'state/order';
import { PaymentProvider } from 'state/payment';
import { ServiceModeProvider } from 'state/service-mode';
import { PageManagerProvider } from 'state/static-page-manager';
import { StoreProvider } from 'state/store';
import { UIProvider } from 'state/ui';
import { WindowSizeProvider } from 'state/window-size';
import { LaunchDarklyFlagsObject } from 'utils/launchdarkly';

declare interface IProviderProps {
  children: ReactNode;
}
const providers = ({
  ldFlags,
  userHasSignedIn,
  userSession,
}: {
  ldFlags: LaunchDarklyFlagsObject;
  userHasSignedIn: boolean;
  userSession: CognitoUserSession | null;
}) => {
  const LDProviderWithFlags = ({ children }: IProviderProps) => LDProvider({ children, ldFlags });
  const AuthProviderWithUserSession = ({ children }: IProviderProps) =>
    AuthProvider({ children, userHasSignedIn, userSession });

  return [
    LoggerProvider,
    LDProviderWithFlags,
    IntlProvider,
    WindowSizeProvider,
    ...(window.Cypress ? [] : [MParticleProvider]),
    LocationProvider,
    BranchProvider,
    GraphQLProvider,
    BrazeProvider,
    LoadingProvider,
    ErrorsProvider,
    NetworkProvider,
    AuthProviderWithUserSession,
    CustomizableContentProvider,
    ServiceModeProvider,
    StoreProvider,
    FeaturesProvider,
    UIProvider,
    DayPartProvider,
    OffersProvider,
    LoyaltyProvider,
    MenuProvider,
    FraudPreventionProvider,
    PaymentProvider,
    PageManagerProvider,
    OrderProvider,
    GeolocationProvider,
    AccessibilityProvider,
    HelmetProvider,
    DeliveryConfirmationProvider,
    OnboardingProvider,
  ];
};

export default providers;
